import React from 'react';
import { PUBLIC_APP_ORIGIN } from '../api';
import { PRESETS, DEFAULT_GENERIC_RULES, DEFAULT_FORM } from '../constants/competition';
import {
  DEFAULT_MEMBERSHIP_TYPE_OPTIONS,
  DEFAULT_PLAYER_TYPE_OPTIONS,
  NON_MEMBER_GUEST_MEMBERSHIP_TYPE,
  MEMBERSHIP_CUSTOM_FIELD_TYPES,
  MEMBERSHIP_NATIVE_FIELD_KEYS,
  MEMBERSHIP_PROFILE_LOCKED_FIELD_KEYS,
  MEMBERSHIP_LABEL_MAX_LENGTH,
  MEMBERSHIP_CONFIG_TEXT_MAX_LENGTH,
} from '../constants/membership';
import { DEFAULT_THEME, THEME_PRESETS } from '../constants/theme';
import { normalizeRulesList, mergeCompDefaults } from '../utils/rules';
import {
  isNonMemberGuestMembershipType,
  normalizeMembershipTypePricing,
  sanitizeMembershipFieldKey,
  sanitizeMembershipCustomType,
  sanitizeMembershipCustomOptions,
  normalizeMembershipFormConfig,
} from '../utils/membership';
import { applyClubTheme } from '../utils/theme';
import TrophySvg from './TrophySvg';
import EmailTemplatesPanel from './EmailTemplatesPanel';
import ClubWebsitePanel from './ClubWebsitePanel';
import ClubMatchSettings from './ClubMatchSettings';
import PosterStudio from './PosterStudio';
import EventsTab from './EventsTab';
import CalendarTab from './CalendarTab';
import NewsletterTab from './NewsletterTab';
import AiAssistantsTab from './AiAssistantsTab';
import MailingListTab from './MailingListTab';
import GalleryTab from './GalleryTab';
import ClubRulesSettings from './ClubRulesSettings';
import MembershipFormBuilder from './MembershipFormBuilder';
import ClubThemeSettings from './ClubThemeSettings';
import ClubGeneralSettings from './ClubGeneralSettings';
import MembershipTypeOptions from './MembershipTypeOptions';
import SiteThemeSettings from './SiteThemeSettings';
import SiteMembershipDefaults from './SiteMembershipDefaults';
import MembershipIntakeTab from './MembershipIntakeTab';
import ClubMembershipQr from './ClubMembershipQr';
import AdminSubscriptions from './AdminSubscriptions';
import ClubPlanPanel from './ClubPlanPanel';
import AdminRegistrationsTab from './AdminRegistrationsTab';
import MemberReviewModal from './MemberReviewModal';
import AdminMembersTab from './AdminMembersTab';

/* ==================================================================== 
   ADMIN PANEL
   ==================================================================== */
/**
 * The admin panel, in one of two scopes.
 *
 * "club" is a club admin's world: their members, registrations, emails,
 * events, posters and settings. "site" is the things that are not about any
 * one club: the clubs themselves, every member across all of them, and the
 * defaults a club inherits when it has not set its own.
 *
 * One component rather than two because the machinery underneath — thirty-odd
 * pieces of state feeding the members table alone — is shared. Splitting the
 * pages meant splitting the navigation, not the code.
 */
function AdminUsersPanel({ API, currentUser, scope = 'club', showToast, showConfirm, onBack, openProfile, selectedClubId: parentClubId, allClubs: parentClubs, setAllClubs: parentSetClubs, customTemplates: parentTemplates, setCustomTemplates: parentSetTemplates, refreshComp, siteTheme, setSiteTheme, onRulesLibraryChange, onNewCompetition }) {
  const isSiteScope = scope === 'site';
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [tab, setTab] = React.useState(scope === 'site' ? 'clubs' : 'users'); // users | registrations | clubs | emails | events | calendar | gallery | posters | newsletter | settings
  const [comps, setComps] = React.useState([]);
  const [selectedCompRegs, setSelectedCompRegs] = React.useState(null);
  const [regs, setRegs] = React.useState([]);
  const [qrData, setQrData] = React.useState(null);
  const [clubs, setClubs] = React.useState([]);
  const [usersClubFilter, setUsersClubFilter] = React.useState('all');
  const [newClubName, setNewClubName] = React.useState('');
  const [mergeSource, setMergeSource] = React.useState(null); // user being merged away
  const [mergeTarget, setMergeTarget] = React.useState(''); // id of user to keep
  const [siteDefaults, setSiteDefaults] = React.useState(null);
  const [clubDefaultsMap, setClubDefaultsMap] = React.useState({}); // { clubId: defaults }
  const [siteThemeForm, setSiteThemeForm] = React.useState(() => siteTheme || { c60: DEFAULT_THEME.c60, c30: DEFAULT_THEME.c30, c10: DEFAULT_THEME.c10 });

  // Sync form when parent loads the saved site theme
  React.useEffect(() => {
    if (siteTheme) setSiteThemeForm(siteTheme);
  }, [siteTheme]);
  const [editingClub, setEditingClub] = React.useState(null); // club being edited
  const [clubEditForm, setClubEditForm] = React.useState({});
  const [websiteThemeSuggestion, setWebsiteThemeSuggestion] = React.useState(null); // { c60,c30,c10 } fetched from club website
  const [membershipOptionPick, setMembershipOptionPick] = React.useState(DEFAULT_MEMBERSHIP_TYPE_OPTIONS[0]);
  const [playerOptionPick, setPlayerOptionPick] = React.useState(DEFAULT_PLAYER_TYPE_OPTIONS[0]);
  const [customMembershipOption, setCustomMembershipOption] = React.useState('');
  const [customPlayerOption, setCustomPlayerOption] = React.useState('');
  const [newMembershipFieldLabel, setNewMembershipFieldLabel] = React.useState('');
  const [newMembershipFieldType, setNewMembershipFieldType] = React.useState('string');
  const [newMembershipFieldStorage, setNewMembershipFieldStorage] = React.useState('email-only');
  const [newMembershipFieldRequired, setNewMembershipFieldRequired] = React.useState(false);
  const [newMembershipFieldPersonal, setNewMembershipFieldPersonal] = React.useState(true);
  const [newMembershipFieldOptions, setNewMembershipFieldOptions] = React.useState('');
  const [newMembershipFieldDetail, setNewMembershipFieldDetail] = React.useState('');
  const [dragMembershipFieldKey, setDragMembershipFieldKey] = React.useState('');
  const [genericRulesDraft, setGenericRulesDraft] = React.useState([]);
  const [clubRulesDraft, setClubRulesDraft] = React.useState([]);
  const [membershipQr, setMembershipQr] = React.useState({ apply: null, renew: null, loading: false });
  const [reviewMember, setReviewMember] = React.useState(null);
  /* The site page has no General tab to land on. */
  const [settingsSubTab, setSettingsSubTab] = React.useState(scope === 'site' ? 'theme' : 'general'); // general | theme | membership | siteMembership | match | rules | plan | emails
  /* Sub-tabs that render their own panel rather than fields inside the club's
     edit card — so the card, and the Edit / Save buttons that drive it, stay
     out of their way. */
  const subTabIsStandalone = ['match', 'rules', 'emails', 'website', 'aiAssistants'].includes(settingsSubTab);

  const getActiveClubId = React.useCallback(() => (
    parentClubId || (currentUser && currentUser.role !== 'super-admin' && currentUser.ClubId)
  ), [parentClubId, currentUser]);

  const getClubById = React.useCallback((id) => {
    if (!id) return null;
    return (parentClubs || clubs).find(c => String(c.id) === String(id)) || null;
  }, [parentClubs, clubs]);

  const startEditingClub = React.useCallback((clubToEdit) => {
    if (!clubToEdit) return;
    setEditingClub(clubToEdit.id);
    setClubEditForm({
      name: clubToEdit.name || '',
      address: clubToEdit.address || '',
      phone: clubToEdit.phone || '',
      email: clubToEdit.email || '',
      clubContacts: (() => {
        const src = (clubToEdit.clubContacts && typeof clubToEdit.clubContacts === 'object') ? clubToEdit.clubContacts : {};
        const others = Array.isArray(src.others)
          ? src.others
              .map(entry => ({
                label: String((entry && entry.label) || '').trim().slice(0, 80),
                email: String((entry && entry.email) || '').trim().slice(0, 120),
              }))
              .filter(entry => entry.label || entry.email)
          : [];
        return {
          presidentEmail: String(src.presidentEmail || '').trim().slice(0, 120),
          vicePresidentEmail: String(src.vicePresidentEmail || '').trim().slice(0, 120),
          treasurerEmail: String(src.treasurerEmail || '').trim().slice(0, 120),
          secretaryEmail: String(src.secretaryEmail || '').trim().slice(0, 120),
          managerEmail: String(src.managerEmail || '').trim().slice(0, 120),
          others,
        };
      })(),
      website: clubToEdit.website || '',
      membershipRenewalMonth: clubToEdit.membershipRenewalMonth || '',
      membershipRenewalDay: clubToEdit.membershipRenewalDay || '',
      seasonStartMonth: clubToEdit.seasonStartMonth || '',
      anniversaryRegion: clubToEdit.anniversaryRegion || '',
      numberOfGreens: clubToEdit.numberOfGreens || 1,
      numberOfRinks: clubToEdit.numberOfRinks || 6,
      membershipTypeOptions: Array.isArray(clubToEdit.membershipTypeOptions) && clubToEdit.membershipTypeOptions.length
        ? [...clubToEdit.membershipTypeOptions]
        : [...DEFAULT_MEMBERSHIP_TYPE_OPTIONS],
      membershipTypePricing: normalizeMembershipTypePricing(
        clubToEdit.membershipTypePricing,
        Array.isArray(clubToEdit.membershipTypeOptions) && clubToEdit.membershipTypeOptions.length
          ? clubToEdit.membershipTypeOptions
          : DEFAULT_MEMBERSHIP_TYPE_OPTIONS
      ),
      membershipTypeDescriptions: { ...(clubToEdit.membershipTypeDescriptions || {}) },
      // Null until the club ticks or unticks one: then the social types are left out.
      playerTypesWithoutStartYear: Array.isArray(clubToEdit.playerTypesWithoutStartYear)
        ? [...clubToEdit.playerTypesWithoutStartYear]
        : null,
      playerTypeOptions: Array.isArray(clubToEdit.playerTypeOptions) && clubToEdit.playerTypeOptions.length
        ? [...clubToEdit.playerTypeOptions]
        : [...DEFAULT_PLAYER_TYPE_OPTIONS],
      membershipFormConfig: normalizeMembershipFormConfig(clubToEdit.membershipFormConfig),
      themeC60: clubToEdit.themeC60 || DEFAULT_THEME.c60,
      themeC30: clubToEdit.themeC30 || DEFAULT_THEME.c30,
      themeC10: clubToEdit.themeC10 || DEFAULT_THEME.c10,
      useDefaultTheme: clubToEdit.useDefaultTheme !== false,
      logo: clubToEdit.logo || '',
      logoPhotoId: clubToEdit.logoPhotoId || null,
    });
    try {
      const saved = localStorage.getItem(`gs_web_theme_${clubToEdit.id}`);
      setWebsiteThemeSuggestion(saved ? JSON.parse(saved) : null);
    } catch {
      setWebsiteThemeSuggestion(null);
    }
    setMembershipOptionPick(DEFAULT_MEMBERSHIP_TYPE_OPTIONS[0]);
    setPlayerOptionPick(DEFAULT_PLAYER_TYPE_OPTIONS[0]);
    setCustomMembershipOption('');
    setCustomPlayerOption('');
  }, []);

  const isSuperAdmin = currentUser.role === 'super-admin';
  const canManageEmails = currentUser.role === 'club-admin' || currentUser.role === 'super-admin';
  const filteredUsers = React.useMemo(() => {
    if (!isSuperAdmin || usersClubFilter === 'all') return users;
    return users.filter(u => String(u.ClubId || '') === String(usersClubFilter));
  }, [users, isSuperAdmin, usersClubFilter]);
  const pendingMembers = React.useMemo(() => (
    filteredUsers.filter(u => String(u.membershipApprovalStatus || '').toLowerCase() === 'pending')
  ), [filteredUsers]);

  React.useEffect(() => {
    fetch(`${API}/api/auth/users`).then(r => r.json()).then(setUsers).catch(() => {}).finally(() => setLoading(false));
    fetch(`${API}/api/competitions`).then(r => r.json()).then(setComps).catch(() => {});
    if (currentUser.role === 'super-admin') {
      fetch(`${API}/api/auth/clubs`).then(r => r.json()).then(setClubs).catch(() => {});
    }
  }, []); // eslint-disable-line

  // Load settings tab data
  const loadSettingsData = React.useCallback(async () => {
    try {
      const clubId = parentClubId || (currentUser && currentUser.role !== 'super-admin' && currentUser.ClubId);
      // Load site defaults
      const siteRes = await fetch(`${API}/api/auth/site-comp-defaults`);
      if (siteRes.ok) setSiteDefaults(await siteRes.json());
      // Load this club's defaults
      if (clubId) {
        const clubRes = await fetch(`${API}/api/auth/clubs/${clubId}/comp-defaults`);
        if (clubRes.ok) {
          const d = await clubRes.json();
          setClubDefaultsMap(prev => ({ ...prev, [clubId]: d }));
        }
      }
      // Load club's templates
      const tRes = await fetch(`${API}/api/templates${clubId ? `?clubId=${clubId}` : ''}`);
      if (tRes.ok) {
        const templates = await tRes.json();
        parentSetTemplates(templates);
      }
    } catch {}
  }, [API, parentClubId, currentUser]); // eslint-disable-line

  React.useEffect(() => {
    if (tab === 'settings') loadSettingsData();
  }, [tab, parentClubId]); // eslint-disable-line

  // Club Settings should open directly in edit mode.
  React.useEffect(() => {
    if (tab !== 'settings') return;
    const clubId = parentClubId || (currentUser && currentUser.role !== 'super-admin' && currentUser.ClubId);
    if (!clubId) return;
    const club = (parentClubs || clubs).find(c => String(c.id) === String(clubId));
    if (!club) return;
    if (editingClub === club.id) return;
    startEditingClub(club);
  }, [tab, parentClubId, currentUser, parentClubs, clubs, editingClub, startEditingClub]);

  React.useEffect(() => {
    if (tab !== 'settings' || settingsSubTab !== 'rules') return;
    const clubId = parentClubId || (currentUser && currentUser.role !== 'super-admin' && currentUser.ClubId);
    setGenericRulesDraft(normalizeRulesList((siteDefaults && siteDefaults.rulesLibraryGeneric) || DEFAULT_GENERIC_RULES, 'site'));
    setClubRulesDraft(normalizeRulesList((clubDefaultsMap[clubId] && clubDefaultsMap[clubId].rulesLibraryClubCustom) || [], 'club'));
  }, [tab, settingsSubTab, parentClubId, currentUser, siteDefaults, clubDefaultsMap]);

  React.useEffect(() => {
    /* Email templates is a sub-tab of Club settings now, so losing the right
       to manage them drops you back to General rather than off the page. */
    if ((settingsSubTab === 'emails' || settingsSubTab === 'website' || settingsSubTab === 'aiAssistants') && !canManageEmails) setSettingsSubTab('general');
    if (tab === 'posters' && !canManageEmails) setTab('users');
    if (tab === 'events' && !canManageEmails) setTab('users');
    if ((tab === 'newsletter' || tab === 'mailingList') && !canManageEmails) setTab('users');
    if (tab === 'gallery' && !canManageEmails) setTab('users');
  }, [tab, settingsSubTab, canManageEmails]);

  React.useEffect(() => {
    if (!isSuperAdmin) {
      setUsersClubFilter('all');
      return;
    }
    if (usersClubFilter === 'all') return;
    const exists = clubs.some(c => String(c.id) === String(usersClubFilter));
    if (!exists) setUsersClubFilter('all');
  }, [isSuperAdmin, usersClubFilter, clubs]);

  const changeRole = async (userId, newRole) => {
    if (userId === currentUser.id) return showToast("Can't change your own role", 'orange');
    const r = await fetch(`${API}/api/auth/users/${userId}/role`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: newRole }),
    });
    if (r.ok) {
      const data = await r.json();
      setUsers(prev => prev.map(u => u.id === userId ? data.user : u));
      showToast(data.message, 'green');
    } else {
      const data = await r.json().catch(() => ({}));
      showToast(data.error || 'Failed to change role', 'red');
    }
  };

  const toggleVerified = async (userId, newStatus) => {
    if (userId === currentUser.id) return showToast("Can't change your own status", 'orange');
    const r = await fetch(`${API}/api/auth/users/${userId}/status`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    if (r.ok) {
      const data = await r.json();
      setUsers(prev => prev.map(u => u.id === userId ? data.user : u));
      showToast(data.message, 'green');
    } else {
      const data = await r.json().catch(() => ({}));
      showToast(data.error || 'Failed to update', 'red');
    }
  };

  const confirmMembership = async (user) => {
    const r = await fetch(`${API}/api/users/${user.id}/confirm-membership`, { method: 'POST' });
    if (r.ok) {
      const data = await r.json();
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, ...data.user, membershipApprovalStatus: 'approved', membershipPendingIntent: null } : u));
      showToast(`${user.membershipPendingIntent === 'renew' ? 'Renewal' : 'Membership'} approved`, 'green');
    } else {
      const data = await r.json().catch(() => ({}));
      showToast(data.error || 'Failed to approve membership', 'red');
    }
  };

  const roleColor = (role) => {
    if (role === 'super-admin') return 'w3-red';
    if (role === 'club-admin') return 'w3-deep-orange';
    return 'w3-theme-l3';
  };

  const getClubOptionsForUser = (user, key) => {
    const allClubRows = [...(parentClubs || []), ...(clubs || [])];
    const clubRow = allClubRows.find(c => String(c.id) === String(user.ClubId));
    if (key === 'membershipType') {
      const options = (clubRow && Array.isArray(clubRow.membershipTypeOptions) && clubRow.membershipTypeOptions.length)
        ? clubRow.membershipTypeOptions
        : DEFAULT_MEMBERSHIP_TYPE_OPTIONS;
      return options.filter(opt => !isNonMemberGuestMembershipType(opt));
    }
    return (clubRow && Array.isArray(clubRow.playerTypeOptions) && clubRow.playerTypeOptions.length)
      ? clubRow.playerTypeOptions
      : DEFAULT_PLAYER_TYPE_OPTIONS;
  };

  const updateUserClassification = async (userId, field, value) => {
    const r = await fetch(`${API}/api/users/${userId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: value || null }),
    });
    if (r.ok) {
      const updated = await r.json();
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...updated } : u));
      showToast('Member updated', 'green');
    } else {
      const data = await r.json().catch(() => ({}));
      showToast(data.error || 'Failed to update member', 'red');
    }
  };

  // Determine which roles this admin can assign
  const availableRoles = isSuperAdmin
    ? ['super-admin', 'club-admin', 'player']
    : ['club-admin', 'player'];

  const loadRegs = async (compId) => {
    setSelectedCompRegs(compId);
    const r = await fetch(`${API}/api/registrations/${compId}`);
    if (r.ok) setRegs(await r.json());
  };

  const handleReg = async (compId, regId, status) => {
    const r = await fetch(`${API}/api/registrations/${compId}/${regId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (r.ok) {
      showToast(`Registration ${status}`, 'green');
      loadRegs(compId);
    }
  };

  const showQR = async (compId) => {
    const baseUrl = PUBLIC_APP_ORIGIN;
    const r = await fetch(`${API}/api/registrations/${compId}/qr?baseUrl=${encodeURIComponent(baseUrl)}`);
    if (r.ok) setQrData(await r.json());
  };

  const createClub = async () => {
    if (!newClubName.trim()) return;
    const r = await fetch(`${API}/api/auth/clubs`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newClubName.trim() }),
    });
    if (r.ok) {
      const club = await r.json();
      setClubs(prev => [...prev, club].sort((a, b) => a.name.localeCompare(b.name)));
      setNewClubName('');
      showToast(`Club "${club.name}" created`, 'green');
    } else {
      const data = await r.json().catch(() => ({}));
      showToast(data.error || 'Failed to create club', 'red');
    }
  };

  const deleteClub = async (clubId, clubName) => {
    if (!(await showConfirm(`Delete "${clubName}"? All users/competitions must be moved first.`))) return;
    const r = await fetch(`${API}/api/auth/clubs/${clubId}`, { method: 'DELETE' });
    if (r.ok) {
      setClubs(prev => prev.filter(c => c.id !== clubId));
      showToast(`Club "${clubName}" deleted`, 'green');
    } else {
      const data = await r.json().catch(() => ({}));
      showToast(data.error || 'Failed to delete club', 'red');
    }
  };

  const mergeUsers = async () => {
    if (!mergeSource || !mergeTarget) return;
    const keepId = parseInt(mergeTarget);
    const removeId = mergeSource.id;
    const keepUser = users.find(u => u.id === keepId);
    if (!keepUser) return showToast('Target user not found', 'red');
    if (!(await showConfirm(`Merge "${mergeSource.name}" into "${keepUser.name}"?\n\nAll competition data, team memberships, and registrations will be transferred to "${keepUser.name}" and "${mergeSource.name}" will be deleted.\n\nThis cannot be undone.`))) return;
    const r = await fetch(`${API}/api/users/${keepId}/merge/${removeId}`, { method: 'POST' });
    if (r.ok) {
      const data = await r.json();
      setUsers(prev => prev.filter(u => u.id !== removeId).map(u => u.id === keepId ? data.user : u));
      setMergeSource(null);
      setMergeTarget('');
      showToast(data.message, 'green');
      // Refresh competition data so merged player shows correctly everywhere
      if (refreshComp) refreshComp();
    } else {
      const data = await r.json().catch(() => ({}));
      showToast(data.error || 'Merge failed', 'red');
    }
  };

  // Every admin tab holds a grid or a settings form, and both want the
  // window: capping them left several hundred pixels empty either side while
  // the content scrolled or wrapped inside the cap. Reading measure is a
  // concern for prose, and there is none here.
  const tabWidthClass = 'content-full';

  return (
    <div className={tabWidthClass}>
      <button className="w3-button w3-theme-l4 w3-round w3-margin-bottom w3-margin-top" style={{ display: 'none' }} onClick={onBack}>
        <i className="fa fa-arrow-left w3-margin-right"></i>Back
      </button>

      <h2 className="page-title">
        <i className={`fa ${isSiteScope ? 'fa-sitemap' : 'fa-lock'} w3-text-theme w3-margin-right`}></i>
        {isSiteScope ? 'Site Administration' : 'Admin Panel'}
      </h2>
      {isSiteScope && (
        <p style={{ marginTop: '-8px', color: 'var(--gs-text-muted)' }}>
          The things that are not about any one club.
        </p>
      )}

      <div className="gs-tabs w3-margin-bottom">
        {/* Site scope leads with the clubs, because that is the thing that
            only exists here. Club scope never shows it at all. */}
        {isSiteScope && isSuperAdmin && (
          <button className={`gs-tab ${tab === 'clubs' ? 'gs-tab-on' : ''}`} onClick={() => setTab('clubs')}>
            Clubs
          </button>
        )}
        <button className={`gs-tab ${tab === 'users' ? 'gs-tab-on' : ''}`} onClick={() => setTab('users')}>
          {isSiteScope ? 'All members' : 'Members'}
        </button>
        {/* Between "approved" and "a member": subs to pay, board to sign
            them off. Its own tab because it is a list somebody works down,
            not a filter of the members list. */}
        {!isSiteScope && (
          <button className={`gs-tab ${tab === 'intake' ? 'gs-tab-on' : ''}`} onClick={() => setTab('intake')}>
            New members
          </button>
        )}
        {!isSiteScope && (
          <button className={`gs-tab ${tab === 'registrations' ? 'gs-tab-on' : ''}`} onClick={() => setTab('registrations')}>
            Registrations
          </button>
        )}
        {isSiteScope && isSuperAdmin && (
          <button className={`gs-tab ${tab === 'settings' ? 'gs-tab-on' : ''}`}
            onClick={() => { setTab('settings'); setSettingsSubTab('theme'); }}>
            Site defaults
          </button>
        )}
        {/* Email templates used to be a tab of its own here. It is a thing a
            club sets up once and revisits rarely — which is what Club settings
            is for — and as a top-level tab it sat between Registrations and
            Events, in the run of tabs somebody works through weekly. */}
        {!isSiteScope && canManageEmails && (
          <>
            <button className={`gs-tab ${tab === 'events' ? 'gs-tab-on' : ''}`} onClick={() => setTab('events')}>
              Events
            </button>
            <button className={`gs-tab ${tab === 'calendar' ? 'gs-tab-on' : ''}`} onClick={() => setTab('calendar')}>
              Calendar
            </button>
            <button className={`gs-tab ${tab === 'gallery' ? 'gs-tab-on' : ''}`} onClick={() => setTab('gallery')}>
              Gallery
            </button>
            <button className={`gs-tab ${tab === 'posters' ? 'gs-tab-on' : ''}`} onClick={() => setTab('posters')}>
              Posters
            </button>
            <button className={`gs-tab ${tab === 'newsletter' ? 'gs-tab-on' : ''}`} onClick={() => setTab('newsletter')}>
              Newsletter
            </button>
            <button className={`gs-tab ${tab === 'mailingList' ? 'gs-tab-on' : ''}`} onClick={() => setTab('mailingList')}>
              Mailing list
            </button>
          </>
        )}
        {!isSiteScope && (
          <button className={`gs-tab ${tab === 'settings' ? 'gs-tab-on' : ''}`} onClick={() => { setTab('settings'); setSettingsSubTab('general'); }}>
            Club settings
          </button>
        )}
      </div>

      {/* ----- Users Tab ----- */}
      {tab === 'users' && <AdminMembersTab API={API} toggleVerified={toggleVerified} availableRoles={availableRoles} changeRole={changeRole} clubs={clubs} confirmMembership={confirmMembership} currentUser={currentUser} filteredUsers={filteredUsers} getClubOptionsForUser={getClubOptionsForUser} isSuperAdmin={isSuperAdmin} loading={loading} openProfile={openProfile} pendingMembers={pendingMembers} roleColor={roleColor} setMergeSource={setMergeSource} setMergeTarget={setMergeTarget} setReviewMember={setReviewMember} setUsers={setUsers} setUsersClubFilter={setUsersClubFilter} showToast={showToast} updateUserClassification={updateUserClassification} users={users} usersClubFilter={usersClubFilter} />}

      {tab === 'users' && reviewMember && <MemberReviewModal confirmMembership={confirmMembership} currentUser={currentUser} getClubById={getClubById} reviewMember={reviewMember} setReviewMember={setReviewMember} users={users} />}

      {/* ----- Merge Modal ----- */}
      {mergeSource && (
        <div className="w3-modal" style={{ display: 'block' }}>
          <div className="w3-modal-content w3-card w3-round w3-animate-top" style={{ maxWidth: '500px' }}>
            <div className="w3-container w3-amber w3-round-top" style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong><i className="fa fa-compress w3-margin-right"></i>Merge Player</strong>
              <button className="w3-button w3-round w3-hover-white" onClick={() => setMergeSource(null)}>&times;</button>
            </div>
            <div className="w3-container w3-padding">
              <p className="w3-small w3-text-grey" style={{ marginTop: 0 }}>
                Transfer all competition data from <strong>{mergeSource.name}</strong> {mergeSource.email && `(${mergeSource.email})`} into another player, then delete this record.
              </p>

              <label className="w3-small w3-text-grey">Merge into (keep this player):</label>
              <select className="w3-select w3-border w3-round w3-margin-bottom" value={mergeTarget}
                onChange={e => setMergeTarget(e.target.value)}>
                <option value="">— Select player to keep —</option>
                {users.filter(u => u.id !== mergeSource.id).sort((a, b) => a.name.localeCompare(b.name)).map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name}{u.email ? ` (${u.email})` : ''}{u.Club ? ` — ${u.Club.name}` : ''}
                  </option>
                ))}
              </select>

              {mergeTarget && (() => {
                const target = users.find(u => u.id === parseInt(mergeTarget));
                return target ? (
                  <div className="w3-panel w3-pale-yellow w3-leftbar w3-border-amber w3-round w3-small" style={{ padding: '10px 14px' }}>
                    <strong>Summary:</strong>
                    <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
                      <li><strong>{mergeSource.name}</strong> will be <span className="w3-text-red">deleted</span></li>
                      <li><strong>{target.name}</strong> will be <span className="w3-text-green">kept</span></li>
                      <li>All competitions, teams, and match history will transfer</li>
                      {!target.email && mergeSource.email && <li>Email <em>{mergeSource.email}</em> will be copied over</li>}
                    </ul>
                  </div>
                ) : null;
              })()}

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
                <button className="w3-button w3-light-grey w3-round" onClick={() => setMergeSource(null)}>Cancel</button>
                <button className="w3-button w3-amber w3-round" disabled={!mergeTarget} onClick={mergeUsers}>
                  <i className="fa fa-compress w3-margin-right"></i>Merge
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ----- Registrations Tab ----- */}
      {tab === 'intake' && (
        <MembershipIntakeTab API={API} clubId={getActiveClubId()} isSuperAdmin={isSuperAdmin}
          openProfile={openProfile} showToast={showToast}
          /* Ticks made there (paid → financial, unblocking) show on the
             members list without a reload. */
          onMembersChanged={changes => setUsers(prev => prev.map(u => (changes[u.id] ? { ...u, ...changes[u.id] } : u)))} />
      )}

      {tab === 'registrations' && <AdminRegistrationsTab comps={comps} handleReg={handleReg} loadRegs={loadRegs} qrData={qrData} regs={regs} selectedCompRegs={selectedCompRegs} setQrData={setQrData} showQR={showQR} />}

      {tab === 'events' && canManageEmails && (
        <EventsTab clubId={getActiveClubId()} showToast={showToast} />
      )}

      {tab === 'calendar' && canManageEmails && (
        <CalendarTab
          clubId={getActiveClubId()}
          /* The club record is already loaded here; the calendar reads its
             anniversary region rather than fetching the club again. */
          anniversaryRegion={(getClubById(getActiveClubId()) || {}).anniversaryRegion || null}
          showToast={showToast}
          onNewCompetition={onNewCompetition} />
      )}

      {tab === 'gallery' && canManageEmails && (
        <GalleryTab clubId={getActiveClubId()} showToast={showToast} showConfirm={showConfirm} />
      )}

      {tab === 'newsletter' && canManageEmails && (
        <NewsletterTab clubId={getActiveClubId()} showToast={showToast} showConfirm={showConfirm} />
      )}
      {tab === 'mailingList' && canManageEmails && (
        <MailingListTab clubId={getActiveClubId()} showToast={showToast} showConfirm={showConfirm} />
      )}

      {tab === 'posters' && canManageEmails && (
        <PosterStudio clubId={getActiveClubId()} clubs={parentClubs || clubs} showToast={showToast} />
      )}

      {/* ----- Clubs Tab (super-admin only) ----- */}
      {tab === 'clubs' && isSuperAdmin && <AdminSubscriptions clubs={clubs} createClub={createClub} deleteClub={deleteClub} newClubName={newClubName} setNewClubName={setNewClubName} showToast={showToast} />}

      {/* ----- Club Settings Tab ----- */}
      {tab === 'settings' && (() => {
        /* The site page shows the site's own defaults, whatever the club
           filter in the footer happens to be set to. Without this, picking a
           club to look at made "Site defaults" quietly show that club's theme
           and logo instead — the same screen, saying something entirely
           different, with nothing to tell you which you were editing. */
        const clubId = isSiteScope
          ? null
          : (parentClubId || (currentUser && currentUser.role !== 'super-admin' && currentUser.ClubId));
        const club = (parentClubs || clubs).find(c => String(c.id) === String(clubId));
        const clubDefaults = clubDefaultsMap[clubId] || {};
        const allTemplates = (parentTemplates || []);
        const isAllClubs = !clubId;
        const isDefaultClub = !!(club && club.slug === 'default-club');
        const isReadOnly = isDefaultClub;
        const canEditSiteRules = isSuperAdmin && isAllClubs;
        const canEditClubRules = !!clubId && !isDefaultClub;

        // Separate system templates (ClubId null) from club templates
        const systemTemplates = allTemplates.filter(t => !t.ClubId);
        const clubTemplates = allTemplates.filter(t => t.ClubId);

        // Merge presets with system overrides and club forks (3 layers)
        const presetLabels = new Set(PRESETS.filter(p => p.settings).map(p => p.label));
        const compTypes = [
          ...PRESETS.filter(p => p.settings).map(p => {
            const sysTmpl = systemTemplates.find(t => t.label === p.label);
            const clubTmpl = clubTemplates.find(t => t.label === p.label);
            // Club fork wins over system, system wins over hardcoded
            const effective = clubTmpl || sysTmpl;
            const effectiveSettings = mergeCompDefaults(DEFAULT_FORM, effective ? effective.settings : p.settings);
            return {
              key: p.key, label: p.label, icon: p.icon,
              settings: effectiveSettings,
              isPreset: true,
              isFork: !!clubTmpl,
              isSystemOverride: !!sysTmpl && !clubTmpl,
              templateId: effective ? effective.id : null,
              systemTemplateId: sysTmpl ? sysTmpl.id : null,
            };
          }),
          ...clubTemplates.filter(t => !presetLabels.has(t.label)).map(t => ({
            key: `template-${t.id}`, label: t.label, icon: t.icon || 'fa-trophy', settings: mergeCompDefaults(DEFAULT_FORM, t.settings), isTemplate: true, templateId: t.id,
          })),
          ...systemTemplates.filter(t => !presetLabels.has(t.label)).map(t => ({
            key: `system-${t.id}`, label: t.label, icon: t.icon || 'fa-trophy', settings: mergeCompDefaults(DEFAULT_FORM, t.settings), isTemplate: true, isSystemTemplate: true, templateId: t.id,
          })),
        ];

        // Columns to show — the key settings a club admin cares about
        const COLS = [
          { key: 'format', label: 'Format', icon: 'fa-users', type: 'select', options: [
            { value: 'singles', label: 'Singles' }, { value: 'pairs', label: 'Pairs' },
            { value: 'triples', label: 'Triples' }, { value: 'fours', label: 'Fours' },
            { value: '2-4-2-pairs', label: '2-4-2' }, { value: 'custom', label: 'Custom' },
          ]},
          { key: 'matchType', label: 'Match Type', icon: 'fa-gavel', type: 'select', options: [
            { value: 'ends', label: 'Ends' }, { value: 'first-to', label: 'First To' },
            { value: 'timed', label: 'Timed' }, { value: 'sets', label: 'Sets' },
          ]},
          { key: 'numberOfEnds', label: 'Ends', icon: 'fa-repeat', type: 'number' },
          { key: 'targetScore', label: 'Target', icon: 'fa-bullseye', tip: 'First To target score — the score a team needs to reach to win the match', type: 'number' },
          { key: 'timeLimit', label: 'Time', icon: 'fa-clock-o', tip: 'Match time limit in minutes (for Timed matches)', type: 'number' },
          { key: 'bowlsPerPlayer', label: 'Bowls', icon: 'fa-circle', type: 'number' },
          { key: 'playersPerTeam', label: 'Players', icon: 'fa-user', type: 'number' },
          { key: 'rinks', label: 'Rinks', icon: 'fa-arrows-h', type: 'number' },
          { key: 'scoringMethod', label: 'Scoring', icon: 'fa-calculator', tip: 'Standard uses only match outcome points. Twilight/Social also adds points for sets won and ends won.', type: 'select', options: [
            { value: 'standard', label: 'Standard' }, { value: 'twilight', label: 'Twilight/Social' },
          ]},
          { key: 'winPoints', label: 'Win', icon: 'fa-trophy', tip: 'Ladder points awarded for a match win', type: 'number' },
          { key: 'drawPoints', label: 'Draw', icon: 'fa-handshake-o', tip: 'Ladder points awarded when a match ends in a draw (not the competition draw)', type: 'number' },
          { key: 'lossPoints', label: 'Loss', icon: 'fa-thumbs-down', tip: 'Ladder points awarded for a match loss', type: 'number' },
          { key: 'byePoints', label: 'Bye', icon: 'fa-pause', tip: 'Ladder points given to a team that has no opponent in a round (sits out)', type: 'number' },
          { key: 'pointsPerSetWon', label: 'Set Bonus', icon: 'fa-clone', tip: 'Extra ladder points per set won (Twilight/Social scoring)', type: 'number' },
          { key: 'pointsPerEndWon', label: 'End Bonus', icon: 'fa-flag-checkered', tip: 'Extra ladder points per end won (Twilight/Social scoring)', type: 'number' },
          { key: 'tournamentType', label: 'Tournament', icon: 'fa-sitemap', tip: 'Round Robin: every team plays every other team.\nKnockout: single elimination, lose once and you\'re out.\nDouble Elimination: must lose twice to be eliminated.', type: 'select', options: [
            { value: 'round-robin', label: 'Round Robin' }, { value: 'knockout', label: 'Knockout' },
            { value: 'double-elimination', label: 'Double Elimination' },
          ]},
          { key: 'extraEndOnDraw', label: 'Extra End', icon: 'fa-plus-circle', tip: 'If enabled, a tied match plays one additional end to decide the winner instead of recording a draw. Twilight/Social: this applies when sets are tied (for example 1 set each).', type: 'bool' },
          { key: 'drawMethod', label: 'Draw', icon: 'fa-random', tip: 'Competition draw method — how match-ups are generated each round.\nRandom: opponents are drawn randomly.\nRated: opponents are balanced by player rating.', type: 'select', options: [
            { value: 'random', label: 'Random' }, { value: 'rating-balanced', label: 'Rated' },
          ]},
          { key: 'teamAssignment', label: 'Teams', icon: 'fa-object-group', tip: 'How players are placed into teams.\nManual: admin assigns teams by hand.\nAuto: players are randomly grouped into teams.\nRated: players are grouped by rating for balanced teams.', type: 'select', options: [
            { value: 'manual', label: 'Manual' }, { value: 'auto-random', label: 'Auto' },
            { value: 'auto-rated', label: 'Rated' },
          ]},
        ];

        const displayVal = (col, val) => {
          if (val === undefined || val === null) return '—';
          if (col.type === 'bool') return val ? 'Yes' : 'No';
          if (col.type === 'select') return col.options.find(o => o.value === val)?.label || val;
          return val;
        };

        const getSettingValue = (settings, keyPath) => keyPath.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), settings);
        const setSettingValue = (settings, keyPath, value) => {
          const next = { ...settings };
          const parts = keyPath.split('.');
          let cursor = next;
          for (let i = 0; i < parts.length - 1; i++) {
            const part = parts[i];
            cursor[part] = { ...(cursor[part] || {}) };
            cursor = cursor[part];
          }
          cursor[parts[parts.length - 1]] = value;
          return next;
        };

        // Relevant columns for a match type
        const isRelevant = (col, settings) => {
          const mt = settings.matchType || 'ends';
          if (col.key === 'numberOfEnds' && mt !== 'ends') return false;
          if (col.key === 'targetScore' && mt !== 'first-to') return false;
          if (col.key === 'timeLimit' && mt !== 'timed') return false;
          if ((col.key === 'numberOfSets' || col.key === 'endsPerSet') && mt !== 'sets') return false;
          if ((col.key === 'pointsPerSetWon' || col.key === 'pointsPerEndWon') && (settings.scoringMethod || 'standard') !== 'twilight') return false;
          return true;
        };

        // Update a comp type: system override, club fork, or update existing template
        const updateCompType = async (ct, colKey, newVal) => {
          const newSettings = setSettingValue(ct.settings, colKey, newVal);

          if (isAllClubs) {
            // Super-admin "All Clubs": always update/create system-level template (ClubId: null)
            const sysId = ct.systemTemplateId || (ct.isSystemOverride || ct.isSystemTemplate ? ct.templateId : null);
            if (sysId) {
              const r = await fetch(`${API}/api/templates/${sysId}`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ settings: newSettings }),
              });
              if (r.ok) {
                const updated = await r.json();
                parentSetTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
              } else showToast('Failed to save', 'red');
            } else {
              // Create new system template
              const r = await fetch(`${API}/api/templates`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ label: ct.label, icon: ct.icon, desc: `System: ${ct.label}`, settings: newSettings, ClubId: null }),
              });
              if (r.ok) {
                const created = await r.json();
                parentSetTemplates(prev => [...prev, created]);
              } else showToast('Failed to save', 'red');
            }
          } else {
            // Club admin
            if (ct.isFork && ct.templateId) {
              // Update existing club fork
              const r = await fetch(`${API}/api/templates/${ct.templateId}`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ settings: newSettings }),
              });
              if (r.ok) {
                const updated = await r.json();
                parentSetTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
              } else showToast('Failed to save', 'red');
            } else if (ct.isTemplate && ct.templateId) {
              // Update existing custom template
              const r = await fetch(`${API}/api/templates/${ct.templateId}`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ settings: newSettings }),
              });
              if (r.ok) {
                const updated = await r.json();
                parentSetTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
              } else showToast('Failed to save', 'red');
            } else if (ct.isPreset && clubId) {
              // Fork preset (or system override) to a club template
              const r = await fetch(`${API}/api/templates`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ label: ct.label, icon: ct.icon, desc: `Customized ${ct.label}`, settings: newSettings, ClubId: clubId }),
              });
              if (r.ok) {
                const created = await r.json();
                parentSetTemplates(prev => [...prev, created]);
                showToast('Customized — saved as club template');
              } else showToast('Failed to save', 'red');
            }
          }
        };

        const revertToPreset = async (ct) => {
          if (!ct.templateId || !ct.isFork) return;
          if (!(await showConfirm(`Revert "${ct.label}" to built-in defaults?`))) return;
          const r = await fetch(`${API}/api/templates/${ct.templateId}`, { method: 'DELETE' });
          if (r.ok) {
            parentSetTemplates(prev => prev.filter(t => t.id !== ct.templateId));
            showToast('Reverted to default');
          }
        };

        const getMembershipConfigFromForm = (formObj) => normalizeMembershipFormConfig(formObj && formObj.membershipFormConfig);
        const getClubContactsFromForm = (formObj) => {
          const src = (formObj && formObj.clubContacts && typeof formObj.clubContacts === 'object') ? formObj.clubContacts : {};
          const others = Array.isArray(src.others)
            ? src.others.map(entry => ({
                label: String((entry && entry.label) || '').slice(0, 80),
                email: String((entry && entry.email) || '').slice(0, 120),
              }))
            : [];
          return {
            presidentEmail: String(src.presidentEmail || '').slice(0, 120),
            vicePresidentEmail: String(src.vicePresidentEmail || '').slice(0, 120),
            treasurerEmail: String(src.treasurerEmail || '').slice(0, 120),
            secretaryEmail: String(src.secretaryEmail || '').slice(0, 120),
            managerEmail: String(src.managerEmail || '').slice(0, 120),
            others,
          };
        };

        const updateClubContactField = (key, value) => {
          setClubEditForm(prev => {
            const contacts = getClubContactsFromForm(prev);
            return {
              ...prev,
              clubContacts: {
                ...contacts,
                [key]: String(value || '').slice(0, 120),
              },
            };
          });
        };

        const updateOtherClubContact = (index, key, value) => {
          setClubEditForm(prev => {
            const contacts = getClubContactsFromForm(prev);
            const nextOthers = contacts.others.map((entry, i) => i === index ? {
              ...entry,
              [key]: String(value || '').slice(0, key === 'label' ? 80 : 120),
            } : entry);
            return {
              ...prev,
              clubContacts: {
                ...contacts,
                others: nextOthers,
              },
            };
          });
        };

        const addOtherClubContact = () => {
          setClubEditForm(prev => {
            const contacts = getClubContactsFromForm(prev);
            return {
              ...prev,
              clubContacts: {
                ...contacts,
                others: [...contacts.others, { label: '', email: '' }],
              },
            };
          });
        };

        const removeOtherClubContact = (index) => {
          setClubEditForm(prev => {
            const contacts = getClubContactsFromForm(prev);
            return {
              ...prev,
              clubContacts: {
                ...contacts,
                others: contacts.others.filter((_, i) => i !== index),
              },
            };
          });
        };

        const updateMembershipFieldConfig = (fieldKey, patch) => {
          setClubEditForm(f => {
            const cfg = getMembershipConfigFromForm(f);
            return {
              ...f,
              membershipFormConfig: {
                ...cfg,
                fields: cfg.fields.map(field => field.key === fieldKey ? { ...field, ...patch } : field),
              },
            };
          });
        };

        const removeMembershipField = (fieldKey) => {
          setClubEditForm(f => {
            const cfg = getMembershipConfigFromForm(f);
            return {
              ...f,
              membershipFormConfig: {
                ...cfg,
                fields: cfg.fields.filter(field => field.key !== fieldKey),
              },
            };
          });
        };

        const reorderMembershipField = (fromFieldKey, toFieldKey) => {
          if (!fromFieldKey || !toFieldKey || fromFieldKey === toFieldKey) return;
          if (MEMBERSHIP_PROFILE_LOCKED_FIELD_KEYS.has(fromFieldKey) || MEMBERSHIP_PROFILE_LOCKED_FIELD_KEYS.has(toFieldKey)) return;
          setClubEditForm(f => {
            const cfg = getMembershipConfigFromForm(f);
            const list = Array.isArray(cfg.fields) ? [...cfg.fields] : [];
            const fromIndex = list.findIndex(field => field.key === fromFieldKey);
            const toIndex = list.findIndex(field => field.key === toFieldKey);
            if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return f;
            const [moved] = list.splice(fromIndex, 1);
            list.splice(toIndex, 0, moved);
            return {
              ...f,
              membershipFormConfig: {
                ...cfg,
                fields: list,
              },
            };
          });
        };

        const moveMembershipFieldByOffset = (fieldKey, offset) => {
          if (!fieldKey || !Number.isInteger(offset) || offset === 0) return;
          if (MEMBERSHIP_PROFILE_LOCKED_FIELD_KEYS.has(fieldKey)) return;
          setClubEditForm(f => {
            const cfg = getMembershipConfigFromForm(f);
            const list = Array.isArray(cfg.fields) ? [...cfg.fields] : [];
            const fromIndex = list.findIndex(field => field.key === fieldKey);
            if (fromIndex < 0) return f;
            const toIndex = fromIndex + offset;
            if (toIndex < 0 || toIndex >= list.length) return f;
            if (MEMBERSHIP_PROFILE_LOCKED_FIELD_KEYS.has(list[toIndex].key)) return f;
            const [moved] = list.splice(fromIndex, 1);
            list.splice(toIndex, 0, moved);
            return {
              ...f,
              membershipFormConfig: {
                ...cfg,
                fields: list,
              },
            };
          });
        };

        const addCustomMembershipField = () => {
          const label = String(newMembershipFieldLabel || '').trim();
          const detail = String(newMembershipFieldDetail || '').trim();
          const type = sanitizeMembershipCustomType(newMembershipFieldType);

          if (type !== 'content' && !label) {
            showToast('Custom field label is required', 'orange');
            return;
          }

          if (type === 'content' && !label && !detail) {
            showToast('Add a heading or text for the content block', 'orange');
            return;
          }

          const options = type === 'select'
            ? sanitizeMembershipCustomOptions(String(newMembershipFieldOptions || '').split(','))
            : [];

          if (type === 'select' && options.length === 0) {
            showToast('Dropdown fields need at least one option', 'orange');
            return;
          }

          setClubEditForm(f => {
            const cfg = getMembershipConfigFromForm(f);
            const usedKeys = new Set((cfg.fields || []).map(field => field.key));
            const keySeed = label || detail.slice(0, 32) || 'custom_field';
            const baseKey = sanitizeMembershipFieldKey(keySeed) || 'custom_field';
            let key = baseKey;
            let suffix = 2;
            while (!key || usedKeys.has(key) || MEMBERSHIP_NATIVE_FIELD_KEYS.has(key)) {
              key = `${baseKey}_${suffix}`;
              suffix += 1;
            }

            return {
              ...f,
              membershipFormConfig: {
                ...cfg,
                fields: [
                  ...(cfg.fields || []),
                  {
                    key,
                    label: (label || (type === 'content' ? 'Text Block' : 'Custom Field')).slice(0, 80),
                    detail: type === 'content' ? detail.slice(0, MEMBERSHIP_CONFIG_TEXT_MAX_LENGTH) : '',
                    type,
                    optionsSource: null,
                    options,
                    enabled: true,
                    required: type === 'content' ? false : !!newMembershipFieldRequired,
                    storage: type === 'content'
                      ? 'email-only'
                      : (newMembershipFieldStorage === 'platform' ? 'platform' : 'email-only'),
                    personal: type === 'content' ? false : !!newMembershipFieldPersonal,
                    isCustom: true,
                  },
                ],
              },
            };
          });

          setNewMembershipFieldLabel('');
          setNewMembershipFieldType('string');
          setNewMembershipFieldStorage('email-only');
          setNewMembershipFieldRequired(false);
          setNewMembershipFieldPersonal(true);
          setNewMembershipFieldOptions('');
          setNewMembershipFieldDetail('');
        };

        const updateClubInfo = async () => {
          if (!clubId) return;
          const r = await fetch(`${API}/api/auth/clubs/${clubId}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(clubEditForm),
          });
          if (r.ok) {
            const updated = await r.json();
            const updater = prev => prev.map(c => c.id === updated.id ? updated : c);
            setClubs(updater);
            if (parentSetClubs) parentSetClubs(updater);
            setEditingClub(null);
            showToast('Club updated');
          } else showToast('Failed to update club', 'red');
        };

        const deleteTemplate = async (templateId) => {
          if (!(await showConfirm('Delete this template?'))) return;
          const r = await fetch(`${API}/api/templates/${templateId}`, { method: 'DELETE' });
          if (r.ok) {
            parentSetTemplates(prev => prev.filter(t => t.id !== templateId));
            showToast('Template deleted');
          } else showToast('Failed to delete template', 'red');
        };

        const clubContacts = getClubContactsFromForm(clubEditForm);
        const membershipTypePricing = normalizeMembershipTypePricing(
          clubEditForm.membershipTypePricing,
          clubEditForm.membershipTypeOptions || DEFAULT_MEMBERSHIP_TYPE_OPTIONS
        );

        return (
          <div>
            {/* The sub-tabs and the club identity share one rule. These used
                to be two stacked bars — a pale one of sub-tabs and, under it,
                a dark green block carrying the club name and an Edit button —
                below the admin tab bar, so a settings page opened three rows
                of chrome before any setting. */}
            {/* Only two of these are site-level. General, Membership and
                Match settings describe one club, and on the site page there is
                no one club for them to describe — they rendered an empty form
                against nothing. */}
            <div className="gs-tabs w3-margin-bottom">
              {!isSiteScope && (
                <button className={`gs-tab ${settingsSubTab === 'general' ? 'gs-tab-on' : ''}`} onClick={() => setSettingsSubTab('general')}>
                  General
                </button>
              )}
              <button className={`gs-tab ${settingsSubTab === 'theme' ? 'gs-tab-on' : ''}`} onClick={() => setSettingsSubTab('theme')}>
                {isSiteScope ? 'Default theme' : 'Theme & logos'}
              </button>
              {!isSiteScope && (
                <button className={`gs-tab ${settingsSubTab === 'membership' ? 'gs-tab-on' : ''}`} onClick={() => setSettingsSubTab('membership')}>
                  Membership
                </button>
              )}
              {/* The site page's own Membership tab: not a club's form, but
                  the option lists every club's form draws on. */}
              {isSiteScope && isSuperAdmin && (
                <button className={`gs-tab ${settingsSubTab === 'siteMembership' ? 'gs-tab-on' : ''}`} onClick={() => setSettingsSubTab('siteMembership')}>
                  Membership
                </button>
              )}
              <button className={`gs-tab ${settingsSubTab === 'match' ? 'gs-tab-on' : ''}`} onClick={() => setSettingsSubTab('match')}>
                Match settings
              </button>
              <button className={`gs-tab ${settingsSubTab === 'rules' ? 'gs-tab-on' : ''}`} onClick={() => setSettingsSubTab('rules')}>
                Rules bank
              </button>
              {/* Club scope only, and deliberately not a sub-tab that says
                  "select a club first". Site-wide templates do not exist: the
                  server refuses a request without a club id, and each club's
                  set is seeded from defaults written in code rather than from
                  anything editable. Making this real means a global-template
                  route and reseeding clubs from it — a feature, not a tab. */}
              {!isSiteScope && canManageEmails && (
                <button className={`gs-tab ${settingsSubTab === 'emails' ? 'gs-tab-on' : ''}`} onClick={() => setSettingsSubTab('emails')}>
                  Email templates
                </button>
              )}
              {/* Club scope only, for the same reason: there is no one club
                  for a site-wide page to be about. */}
              {!isSiteScope && canManageEmails && (
                <button className={`gs-tab ${settingsSubTab === 'website' ? 'gs-tab-on' : ''}`} onClick={() => setSettingsSubTab('website')}>
                  Club website
                </button>
              )}
              {/* What the club's AI helpers are told. Club scope only: the
                  page is about one club's settings as well as the
                  instructions. */}
              {!isSiteScope && canManageEmails && (
                <button className={`gs-tab ${settingsSubTab === 'aiAssistants' ? 'gs-tab-on' : ''}`} onClick={() => setSettingsSubTab('aiAssistants')}>
                  AI Assistants
                </button>
              )}
              {!isSiteScope && (
                <button className={`gs-tab ${settingsSubTab === 'plan' ? 'gs-tab-on' : ''}`} onClick={() => setSettingsSubTab('plan')}>
                  Plan
                </button>
              )}
              {club && (
                <span className="gs-tabs-end">
                  {club.logo && (
                    <img src={club.logo} alt="" style={{ width: 26, height: 26, objectFit: 'contain', borderRadius: 4 }} />
                  )}
                  <span style={{ fontWeight: 600, fontSize: 'var(--gs-text-sm)' }}>{club.name}</span>
                  {!subTabIsStandalone && (
                    editingClub === club.id ? (
                      <>
                        <button className="w3-button w3-round"
                          style={{ minHeight: 'var(--gs-control-sm)', background: 'var(--gs-accent-fill, var(--club-30))', color: '#fff', fontWeight: 600 }}
                          onClick={updateClubInfo}>
                          Save
                        </button>
                        <button className="w3-button w3-round w3-theme-l4" style={{ minHeight: 'var(--gs-control-sm)' }}
                          onClick={() => setEditingClub(null)}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button className="w3-button w3-round w3-theme-l4" style={{ minHeight: 'var(--gs-control-sm)' }}
                        onClick={() => startEditingClub(club)}>
                        <i className="fa fa-pencil w3-margin-right"></i>Edit
                      </button>
                    )
                  )}
                </span>
              )}
            </div>

            {/* --- Club Info ---
                The club name and the Edit / Save / Cancel buttons that used
                to head this card now live on the tab rule above, so what is
                left is the settings themselves. */}
            {club && !subTabIsStandalone && (
              <div className="gs-panel w3-margin-bottom">
                <div>
                  {editingClub === club.id ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
                      {settingsSubTab === 'plan' && <ClubPlanPanel clubId={getActiveClubId()} />}
                      {settingsSubTab === 'general' && <ClubGeneralSettings addOtherClubContact={addOtherClubContact} club={club} clubContacts={clubContacts} clubEditForm={clubEditForm} removeOtherClubContact={removeOtherClubContact} setClubEditForm={setClubEditForm} updateClubContactField={updateClubContactField} updateOtherClubContact={updateOtherClubContact} />}
                      {settingsSubTab === 'theme' && <ClubThemeSettings API={API} club={club} clubEditForm={clubEditForm} clubs={clubs} currentUser={currentUser} setClubEditForm={setClubEditForm} setWebsiteThemeSuggestion={setWebsiteThemeSuggestion} showToast={showToast} siteTheme={siteTheme} websiteThemeSuggestion={websiteThemeSuggestion} />}
                      {settingsSubTab === 'membership' && <MembershipTypeOptions clubEditForm={clubEditForm} customMembershipOption={customMembershipOption} customPlayerOption={customPlayerOption} membershipOptionPick={membershipOptionPick} membershipTypePricing={membershipTypePricing} playerOptionPick={playerOptionPick} setClubEditForm={setClubEditForm} setCustomMembershipOption={setCustomMembershipOption} setCustomPlayerOption={setCustomPlayerOption} setMembershipOptionPick={setMembershipOptionPick} setPlayerOptionPick={setPlayerOptionPick} showToast={showToast} users={users} />}
                      {settingsSubTab === 'membership' && <MembershipFormBuilder addCustomMembershipField={addCustomMembershipField} club={club} clubEditForm={clubEditForm} dragMembershipFieldKey={dragMembershipFieldKey} getMembershipConfigFromForm={getMembershipConfigFromForm} moveMembershipFieldByOffset={moveMembershipFieldByOffset} newMembershipFieldDetail={newMembershipFieldDetail} newMembershipFieldLabel={newMembershipFieldLabel} newMembershipFieldOptions={newMembershipFieldOptions} newMembershipFieldPersonal={newMembershipFieldPersonal} newMembershipFieldRequired={newMembershipFieldRequired} newMembershipFieldStorage={newMembershipFieldStorage} newMembershipFieldType={newMembershipFieldType} removeMembershipField={removeMembershipField} reorderMembershipField={reorderMembershipField} setClubEditForm={setClubEditForm} setDragMembershipFieldKey={setDragMembershipFieldKey} setNewMembershipFieldDetail={setNewMembershipFieldDetail} setNewMembershipFieldLabel={setNewMembershipFieldLabel} setNewMembershipFieldOptions={setNewMembershipFieldOptions} setNewMembershipFieldPersonal={setNewMembershipFieldPersonal} setNewMembershipFieldRequired={setNewMembershipFieldRequired} setNewMembershipFieldStorage={setNewMembershipFieldStorage} setNewMembershipFieldType={setNewMembershipFieldType} updateMembershipFieldConfig={updateMembershipFieldConfig} users={users} />}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px' }}>
                      {[
                        { label: 'Address', value: club.address, icon: 'fa-map-marker' },
                        { label: 'Phone', value: club.phone, icon: 'fa-phone' },
                        { label: 'Email', value: club.email, icon: 'fa-envelope' },
                        { label: 'Website', value: club.website, icon: 'fa-globe' },
                        { label: 'Greens', value: club.numberOfGreens || 1, icon: 'fa-th-large' },
                        { label: 'Rinks', value: club.numberOfRinks || 6, icon: 'fa-columns' },
                        {
                          label: 'Renewal Date',
                          value: (club.membershipRenewalMonth && club.membershipRenewalDay)
                            ? `${club.membershipRenewalDay}/${club.membershipRenewalMonth}`
                            : 'Rolling (12 months)',
                          icon: 'fa-calendar',
                        },
                        {
                          label: 'President',
                          value: (club.clubContacts && club.clubContacts.presidentEmail) || '',
                          icon: 'fa-user',
                        },
                        {
                          label: 'Vice President',
                          value: (club.clubContacts && club.clubContacts.vicePresidentEmail) || '',
                          icon: 'fa-user-o',
                        },
                        {
                          label: 'Treasurer',
                          value: (club.clubContacts && club.clubContacts.treasurerEmail) || '',
                          icon: 'fa-calculator',
                        },
                        {
                          label: 'Secretary',
                          value: (club.clubContacts && club.clubContacts.secretaryEmail) || '',
                          icon: 'fa-envelope',
                        },
                        {
                          label: 'Club Manager',
                          value: (club.clubContacts && club.clubContacts.managerEmail) || '',
                          icon: 'fa-briefcase',
                        },
                        { label: 'Membership Types', value: (club.membershipTypeOptions || DEFAULT_MEMBERSHIP_TYPE_OPTIONS).join(', '), icon: 'fa-id-card' },
                        { label: 'Player Types', value: (club.playerTypeOptions || DEFAULT_PLAYER_TYPE_OPTIONS).join(', '), icon: 'fa-users' },
                        {
                          label: 'Membership Form',
                          value: (() => {
                            const cfg = normalizeMembershipFormConfig(club.membershipFormConfig);
                            const visibleCount = cfg.fields.filter(f => f.enabled).length;
                            return `${cfg.enabled ? 'Enabled' : 'Disabled'} (${visibleCount} fields)`;
                          })(),
                          icon: 'fa-wpforms',
                        },
                      ].map(item => (
                        <span key={item.label} className="w3-small">
                          <i className={`fa ${item.icon} w3-text-grey w3-margin-right`}></i>
                          {item.value || <span className="w3-text-light-grey">Not set</span>}
                        </span>
                      ))}
                      <span className="w3-small" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <i className="fa fa-paint-brush w3-text-grey"></i>
                        {[club.themeC60 || DEFAULT_THEME.c60, club.themeC30 || DEFAULT_THEME.c30, club.themeC10 || DEFAULT_THEME.c10].map((hex, i) => (
                          <span key={i} title={['60% bg', '30% navbar', '10% accent'][i] + ': ' + hex}
                            style={{ display: 'inline-block', width: '18px', height: '18px', borderRadius: '4px', background: hex, border: '1px solid rgba(0,0,0,.15)', verticalAlign: 'middle' }} />
                        ))}
                        <span className="w3-text-grey">Theme</span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {club && settingsSubTab === 'membership' && <ClubMembershipQr API={API} club={club} clubs={clubs} loading={loading} membershipQr={membershipQr} setMembershipQr={setMembershipQr} showToast={showToast} />}

            {/* --- Site Membership Defaults (super-admin) --- */}
            {settingsSubTab === 'siteMembership' && isSiteScope && isSuperAdmin && <SiteMembershipDefaults API={API} showToast={showToast} />}

            {/* --- Site Default Theme (super-admin, All Clubs mode) --- */}
            {settingsSubTab === 'theme' && isAllClubs && isSuperAdmin && <SiteThemeSettings API={API} club={club} clubs={clubs} isAllClubs={isAllClubs} isSuperAdmin={isSuperAdmin} setSiteTheme={setSiteTheme} setSiteThemeForm={setSiteThemeForm} showToast={showToast} siteThemeForm={siteThemeForm} />}

            {/* --- Rules Bank --- */}
            {settingsSubTab === 'rules' && <ClubRulesSettings API={API} canEditClubRules={canEditClubRules} canEditSiteRules={canEditSiteRules} club={club} clubDefaultsMap={clubDefaultsMap} clubId={clubId} clubRulesDraft={clubRulesDraft} clubs={clubs} genericRulesDraft={genericRulesDraft} isAllClubs={isAllClubs} isDefaultClub={isDefaultClub} onRulesLibraryChange={onRulesLibraryChange} setClubDefaultsMap={setClubDefaultsMap} setClubRulesDraft={setClubRulesDraft} setGenericRulesDraft={setGenericRulesDraft} setSiteDefaults={setSiteDefaults} showToast={showToast} siteDefaults={siteDefaults} />}

            {/* --- Email Templates ---
                No club id passed from the footer's filter: the panel already
                treats a super-admin with no club selected as editing the
                templates every club starts from, and passing the filter
                through here would have made the site page edit that club's
                templates instead — the same fault the settings tab had. */}
            {settingsSubTab === 'emails' && canManageEmails && (
              <EmailTemplatesPanel
                API={API}
                showToast={showToast}
                showConfirm={showConfirm}
                selectedClubId={parentClubId}
                isSuperAdmin={isSuperAdmin}
              />
            )}

            {/* --- The club's own public page --- */}
            {settingsSubTab === 'website' && canManageEmails && (
              <ClubWebsitePanel clubId={getActiveClubId()} showToast={showToast}
                onOpenPlan={() => setSettingsSubTab('plan')} />
            )}

            {/* --- What the club's AI helpers are told --- */}
            {settingsSubTab === 'aiAssistants' && canManageEmails && (
              <AiAssistantsTab clubId={getActiveClubId()} />
            )}

            {/* --- Competition Types & Defaults --- */}
            {settingsSubTab === 'match' && <ClubMatchSettings COLS={COLS} club={club} compTypes={compTypes} deleteTemplate={deleteTemplate} displayVal={displayVal} getSettingValue={getSettingValue} isAllClubs={isAllClubs} isReadOnly={isReadOnly} isRelevant={isRelevant} revertToPreset={revertToPreset} updateCompType={updateCompType} />}
          </div>
        );
      })()}
    </div>
  );
}

export default AdminUsersPanel;
